"use client"

import { Fragment, useState } from "react"
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react"
import { Menu, X, ChevronRight, User, Truck } from "lucide-react"
import useToggleState from "@lib/hooks/use-toggle-state"

import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { getHandleHref } from "@modules/common/utils/get-handle-href"
import CountrySelect from "../country-select"
import LanguageSelect from "../language-select"
import { HttpTypes } from "@medusajs/types"
import { Locale } from "@lib/data/locales"
import { UTILITY_NAV } from "@constants/navigation"

type SideMenuProps = {
  regions: HttpTypes.StoreRegion[] | null
  locales: Locale[] | null
  currentLocale: string | null
  categories?: HttpTypes.StoreProductCategory[] | null
}

const UTILITY_ICONS: Record<string, React.ElementType> = {
  Account: User,
  "Track My Order": Truck,
}

const SideMenu = ({
  regions,
  locales,
  currentLocale,
  categories,
}: SideMenuProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const countryToggleState = useToggleState()
  const languageToggleState = useToggleState()

  const open = () => setIsOpen(true)
  const close = () => setIsOpen(false)

  /** Top-level categories only (no parent) */
  const topCategories =
    categories?.filter((c) => !c.parent_category) ?? []

  return (
    <>
      <button
        onClick={open}
        className="flex items-center justify-center p-2 text-grey-70 hover:text-brand-primary transition-colors"
        aria-label="Open navigation menu"
        data-testid="nav-menu-button"
      >
        <Menu className="w-6 h-6" />
      </button>

      <Transition show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[100]"
          onClose={close}
        >
          {/* Backdrop */}
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40" />
          </TransitionChild>

          {/* Slide-in panel from left */}
          <div className="fixed inset-0 flex">
            <TransitionChild
              as={Fragment}
              enter="transform transition ease-out duration-300"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in duration-200"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <DialogPanel className="relative w-full max-w-xs bg-white shadow-xl flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-brand-primary text-white">
                  <span className="font-semibold text-base">Menu</span>
                  <button
                    onClick={close}
                    className="p-1 hover:bg-white/10 rounded transition-colors"
                    aria-label="Close menu"
                    data-testid="close-menu-button"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Scrollable content */}
                <div className="flex-1 overflow-y-auto">
                  {/* Categories from Medusa */}
                  {topCategories.length > 0 && (
                    <nav aria-label="Product categories">
                      <div className="px-4 py-3 border-b border-neutral-200">
                        <span className="text-xs font-semibold uppercase tracking-wider text-grey-40">
                          Shop by Category
                        </span>
                      </div>
                      <ul className="py-1">
                        {topCategories.map((category) => (
                          <li key={category.id}>
                            <LocalizedClientLink
                              href={getHandleHref(category.handle)}
                              onClick={close}
                              className="flex items-center justify-between px-4 py-3 text-sm text-grey-80 hover:bg-neutral-50 hover:text-brand-primary transition-colors"
                              data-testid={`mobile-category-${category.handle}`}
                            >
                              <span>{category.name}</span>
                              <ChevronRight className="w-4 h-4 text-grey-30" />
                            </LocalizedClientLink>
                          </li>
                        ))}
                      </ul>
                    </nav>
                  )}

                  {/* Sale link */}
                  <div className="border-t border-neutral-200">
                    <LocalizedClientLink
                      href="/store"
                      onClick={close}
                      className="flex items-center px-4 py-3 text-sm font-semibold text-brand-accent hover:bg-red-50 transition-colors"
                    >
                      Sale
                    </LocalizedClientLink>
                  </div>

                  {/* Utility links */}
                  <div className="border-t border-neutral-200">
                    <div className="px-4 py-3 border-b border-neutral-200">
                      <span className="text-xs font-semibold uppercase tracking-wider text-grey-40">
                        My Account
                      </span>
                    </div>
                    <ul className="py-1">
                      {UTILITY_NAV.map((item) => {
                        const Icon = UTILITY_ICONS[item.label]
                        return (
                          <li key={item.label}>
                            <LocalizedClientLink
                              href={item.href}
                              onClick={close}
                              className="flex items-center gap-3 px-4 py-3 text-sm text-grey-80 hover:bg-neutral-50 hover:text-brand-primary transition-colors"
                            >
                              {Icon && (
                                <Icon className="w-4 h-4 text-grey-40" />
                              )}
                              <span>{item.label}</span>
                            </LocalizedClientLink>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                </div>

                {/* Bottom: Country + Language selectors */}
                <div className="border-t border-neutral-200 p-4 space-y-4 bg-neutral-50">
                  {!!locales?.length && (
                    <div
                      onMouseEnter={languageToggleState.open}
                      onMouseLeave={languageToggleState.close}
                    >
                      <LanguageSelect
                        toggleState={languageToggleState}
                        locales={locales}
                        currentLocale={currentLocale}
                      />
                    </div>
                  )}
                  {regions && (
                    <div
                      onMouseEnter={countryToggleState.open}
                      onMouseLeave={countryToggleState.close}
                    >
                      <CountrySelect
                        toggleState={countryToggleState}
                        regions={regions}
                      />
                    </div>
                  )}
                  <p className="text-xs text-grey-40 text-center">
                    &copy; {new Date().getFullYear()} UK Soccer Shop. All rights
                    reserved.
                  </p>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}

export default SideMenu
